/* Display-only countdown: no price, discount, cart, storage or network writes. */
(() => {
  const tagName = 'velorart-product-countdown';
  if (customElements.get(tagName)) return;
  const day = 86400000;

  class VelorartProductCountdown extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      queueMicrotask(() => {
        if (this.isConnected && this._connected && !this._started) this.start();
      });
    }

    start() {
      this._values = ['days', 'hours', 'minutes', 'seconds'].map(unit => this.querySelector(`[data-va-time="${unit}"]`));
      if (this._values.some(node => !node)) return;
      this._timerElement = this.querySelector('[role="timer"]');
      this._periodStart = null;
      this._periodEnd = null;
      this._mode = this.dataset.mode;
      try {
        if (this._mode === 'repeat') {
          const days = Number(this.dataset.cycleDays);
          const iso = this.dataset.cycleStart || '';
          // Explicit offsets prevent the visitor's local zone changing the anchor.
          const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:?\d{2})$/i.exec(iso);
          this._anchor = Date.parse(iso);
          if (!Number.isInteger(days) || days < 1 || days > 30 || !match || !Number.isFinite(this._anchor)) {
            throw new Error('请检查循环天数及循环起点；起点须为含时区的 ISO 日期时间，例如 2026-10-29T15:09:01-04:00。');
          }
          const [year, month, date, hour, minute, second] = match.slice(1, 7).map(value => Number(value || 0));
          const validDate = new Date(Date.UTC(year, month - 1, date));
          if (year < 2000 || year > 9998 || month < 1 || month > 12 || date < 1 || validDate.getUTCMonth() !== month - 1 || validDate.getUTCDate() !== date || hour > 23 || minute > 59 || second > 59) {
            throw new Error('循环起点不是有效日期时间，请检查日期和时分秒。');
          }
          this._duration = days * day;
          this._calendar = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/New_York', calendar: 'gregory', numberingSystem: 'latn',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
          });
          this._anchorLocal = this.localTime(this._anchor);
        } else if (this._mode === 'metafield') {
          const seconds = Number(this.dataset.deadline);
          if (!Number.isSafeInteger(seconds) || seconds <= 0) {
            throw new Error('请为当前产品设置 custom.countdown_timer 日期时间元字段，或切换为按天自动循环。');
          }
          this._deadline = seconds * 1000;
        } else {
          throw new Error('请选择有效的倒计时模式。');
        }
      } catch (error) {
        this.showNotice(error.message || '倒计时设置无效，请检查设置。');
        return;
      }
      this._started = true;
      this._onResume = () => this.resume();
      this._onVisibility = () => document.hidden ? this.stop() : this.resume();
      document.addEventListener('visibilitychange', this._onVisibility);
      window.addEventListener('pageshow', this._onResume);
      this.resume();
    }

    localTime(timestamp) {
      const parts = this._calendar.formatToParts(timestamp);
      const value = name => Number(parts.find(part => part.type === name).value);
      return Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'), value('second'));
    }

    cycleBoundary(index) {
      if (index === 0) return this._anchor;
      // Add New York calendar days, then resolve the new local date through DST.
      const local = this._anchorLocal + index * this._duration;
      const candidates = [-day, day].map(delta => {
        const probe = local + delta;
        const instant = local - (this.localTime(probe) - probe);
        return { instant, local: this.localTime(instant) };
      });
      const matches = candidates.filter(candidate => candidate.local === local);
      // Repeated hour: first occurrence. Skipped hour: advance by the DST gap.
      if (matches.length) return Math.min(...matches.map(candidate => candidate.instant));
      return candidates.filter(candidate => candidate.local > local).sort((a, b) => a.local - b.local)[0].instant;
    }

    update() {
      let remaining;
      if (this._mode === 'repeat') {
        const now = Math.max(Date.now(), this._anchor);
        if (this._periodEnd === null || now < this._periodStart || now >= this._periodEnd) {
          let index = Math.max(0, Math.floor((this.localTime(now) - this._anchorLocal) / this._duration));
          let start = this.cycleBoundary(index);
          while (index > 0 && now < start) start = this.cycleBoundary(--index);
          let end = this.cycleBoundary(index + 1);
          while (now >= end) {
            start = end;
            end = this.cycleBoundary(++index + 1);
          }
          this._periodStart = start;
          this._periodEnd = end;
        }
        remaining = this._periodEnd - now;
      } else {
        remaining = this._deadline - Date.now();
        if (remaining <= 0) {
          this.showNotice('当前产品的倒计时已结束；正式页面自动隐藏本区块。');
          return false;
        }
      }
      const total = Math.ceil(remaining / 1000);
      const values = [Math.floor(total / 86400), Math.floor(total / 3600) % 24, Math.floor(total / 60) % 60, total % 60];
      values.forEach((value, index) => {
        const text = String(value).padStart(2, '0');
        if (this._values[index].textContent !== text) this._values[index].textContent = text;
      });
      const labels = ['days', 'hours', 'minutes', 'seconds'];
      const label = values.map((value, index) => `${value} ${labels[index]}`).join(', ');
      if (this._timerElement && this._timerElement.getAttribute('aria-label') !== label) this._timerElement.setAttribute('aria-label', label);
      if (this._noticeVisible) {
        this.querySelectorAll('[data-va-display]').forEach(node => { node.hidden = false; });
        const notice = this.querySelector('[data-va-notice]');
        if (notice) notice.hidden = true;
        this._noticeVisible = false;
      }
      this.hidden = false;
      return true;
    }

    showNotice(message) {
      this.stop();
      this.hidden = true;
      if (this.dataset.designMode !== 'true') return;
      const notice = this.querySelector('[data-va-notice]');
      if (!notice) return;
      this.querySelectorAll('[data-va-display]').forEach(node => { node.hidden = true; });
      notice.textContent = message;
      notice.hidden = false;
      this._noticeVisible = true;
      this.hidden = false;
    }

    resume() {
      this.stop();
      if (!this.isConnected || !this._started || this.update() === false) return;
      if (!document.hidden) this._timer = setTimeout(() => this.resume(), 1010 - (Date.now() % 1000));
    }

    stop() {
      clearTimeout(this._timer);
      this._timer = null;
    }

    disconnectedCallback() {
      this.stop();
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._onResume) window.removeEventListener('pageshow', this._onResume);
      this._connected = false;
      this._started = false;
    }
  }
  customElements.define(tagName, VelorartProductCountdown);
})();
